import { List, ListItem, Tooltip } from "@material-ui/core";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

const CustomSelectFilter = ({
  value = [],
  handleChange,
  list,
  title,
  placeHolder,
  isOpen,
  toggleDropdown,
  resetMultiSelect,
  setResetMultiSelect,
  singleSelect = false,
  listPosition,
  onChipRemove,
}) => {
  const [selectedValues, setSelectedValues] = useState(
    singleSelect ? value || "" : value || [],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredList, setFilteredList] = useState(list.slice(0, 50));
  const dropdownRef = useRef(null);

  // const pl3List = useSelector((state) => state?.filter?.filteredList?.PL3);

  /* ---------------- Close on outside click ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        toggleDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, toggleDropdown]);

  /* ---------------- Search filter ---------------- */
  useEffect(() => {
    if (!searchQuery) {
      setFilteredList(list.slice(0, 50));
    } else {
      const filtered = list.filter((item) =>
        item?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredList(filtered);
    }
  }, [searchQuery, list]);

  /* ---------------- Reset handling ---------------- */
  useEffect(() => {
    if (resetMultiSelect) {
      setSelectedValues(singleSelect ? "" : []);
      setResetMultiSelect(false);
    } else {
      setSelectedValues(value);
    }
  }, [resetMultiSelect, value, singleSelect, setResetMultiSelect]);

  /* ---------------- Selection logic ---------------- */
  const handleOptionClick = (option) => {
    if (singleSelect) {
      setSelectedValues(option);
      handleChange(option);
      toggleDropdown();
    } else {
      setSelectedValues((prev) =>
        prev.includes(option)
          ? prev.filter((v) => v !== option)
          : [...prev, option],
      );
    }
  };

  const handleApply = () => {
    handleChange(selectedValues);
    toggleDropdown();
  };

  const handleChipRemove = (option, e) => {
    e.stopPropagation();
    setSelectedValues((prev) => {
      const updatedVal = prev.filter((item) => item !== option);
      if (onChipRemove) {
        onChipRemove(title, updatedVal);
      }
      return updatedVal;
    });
  };

  /* ---------------- Render row ---------------- */
  const renderRow = (item) => {
    const isSelected = singleSelect
      ? selectedValues === item
      : selectedValues.includes(item);

    return (
      <Tooltip key={item} arrow placement="right" title={item}>
        <ListItem
          className={`custom-select-filter-option ${isSelected ? "selected" : ""}`}
          onClick={() => handleOptionClick(item)}
        >
          {item}
        </ListItem>
      </Tooltip>
    );
  };

  return (
    <div className="custom-select-filter" ref={dropdownRef}>
      {/* ================= HEADER ================= */}
      <div className="custom-select-filter-header" onClick={toggleDropdown}>
        {singleSelect ? (
          <span>{selectedValues || placeHolder}</span>
        ) : selectedValues.length ? (
          <div className="chip-wrapper">
            {selectedValues.slice(0, 3).map((item, index) => (
              <Tooltip key={index} title={item} arrow>
                <span className="chip">
                  <span className="chip-truncate">{item}</span>

                  <span
                    className="chip-remove"
                    onClick={(e) => handleChipRemove(item, e)}
                  >
                    ✖
                  </span>
                </span>
              </Tooltip>
            ))}

            {selectedValues.length > 3 && (
              <span className="chip chip-count">
                +{selectedValues.length - 3}
              </span>
            )}
          </div>
        ) : (
          <span>{placeHolder}</span>
        )}
      </div>

      {/* ================= DROPDOWN ================= */}
      {isOpen && (
        <div
          className="custom-select-filter-dropdown"
          style={{
            top: listPosition === "top" ? "auto" : "100%",
            bottom: listPosition === "top" ? "100%" : "auto",
          }}
        >
          <input
            className="custom-select-filter-search"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="dropdown-list">
            {filteredList.length ? (
              <List>{filteredList.map(renderRow)}</List>
            ) : (
              <div className="custom-select-filter-no-options">
                No options found
              </div>
            )}
          </div>

          {!singleSelect && (
            <div className="apply-footer">
              <button
                className="custom-select-filter-apply"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}

      {/* ================= CSS ================= */}
      <style>{`
        .custom-select-filter {
          position: relative;
          width: 300px;
          font-family: Poppins, sans-serif;
        }

        .custom-select-filter-header {
          border: 1px solid #ccc;
          border-radius: 4px;
          height: 36px;
          padding: 4px 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          overflow: hidden;
          background: #fff;
        }

        /* ---------- Chips ---------- */
        .chip-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
          max-width: 100%;
          overflow: hidden;
        }

        /* chip container */
        .chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
           background: lightblue;
          padding: 2px 8px;
          border-radius: 16px;
          max-width: 150px; /* controls chip width */
          min-width: 0; /* important for truncation */
        }
        
        /* text truncate */
        .chip .chip-truncate {
          max-width: 100px;
          overflow: hidden;
             font-size:12px;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }
        
        /* remove icon */
        .chip-remove {
          cursor: pointer;
          flex-shrink: 0;
          font-size: 12px;
        }

        .chip-count {
          background: #cfe3ff;
          font-weight: 600;
          flex-shrink: 0;
           font-size:12px;
        }

        /* ---------- Dropdown ---------- */
        .custom-select-filter-dropdown {
          position: absolute;
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 4px;
          z-index: 9999;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          display: flex;
          flex-direction: column;
          max-height: 300px;
        }

        .custom-select-filter-search {
          padding: 8px;
          border: none;
          border-bottom: 1px solid #ddd;
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
        }

        .dropdown-list {
          overflow-y: auto;
          flex: 1;
        }

        .custom-select-filter-option {
          cursor: pointer;
          padding: 8px;
        }

        .custom-select-filter-option:hover {
          background: #f2f2f2;
        }

        .custom-select-filter-option.selected {
          background: #d9e8ff;
        }

        /* ---------- Apply Button ---------- */
        .apply-footer {
          position: sticky;
          bottom: 0;
          background: white;
          padding: 8px;
          border-top: 1px solid #ddd;
        }

        .custom-select-filter-apply {
          width: 100%;
          padding: 8px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .custom-select-filter-apply:hover {
          background: #0056b3;
        }

        .chip-remove {
          margin-left: 4px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default CustomSelectFilter;
